import React from 'react'
import { EditButtons, MetaField, fieldInput } from './MetaField'

export const formatMetricValue = (val, unit = '') => {
  if (val === null || val === undefined) return '—'
  const n = parseFloat(val)
  if (Number.isNaN(n)) return '—'
  return `${n.toFixed(2)}${unit ? ` ${unit}` : ''}`
}

const HikvisionDeviceParams = ({
  device,
  metrics,
  serial,
  canManage,
  t,
  editingField,
  draft,
  setDraft,
  startEdit,
  onCancelEdit,
  onSave,
  isSaving,
  saveError,
  extraFields,
  onCheckReachability,
  isChecking,
  checkResult,
  onDelete,
  isDeleting,
  deleteError,
}) => {
  const copySerial = () => {
    if (!serial || !navigator.clipboard?.writeText) return
    navigator.clipboard.writeText(serial).catch(() => {})
  }

  return (
    <div className="grid max-w-4xl grid-cols-1 gap-x-12 gap-y-2 rounded-2xl bg-surface-1 p-5 sm:grid-cols-2">
      <MetaField
        label="Название"
        editing={editingField === 'name'}
        canEdit={canManage}
        onStartEdit={() => startEdit('name', { name: device.name || '' })}
        editTitle={t('common.edit')}
        editor={
          <>
            <input
              type="text"
              value={draft.name ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              className={fieldInput}
              disabled={isSaving}
            />
            <EditButtons
              onSave={() => onSave({ name: draft.name?.trim() })}
              onCancel={onCancelEdit}
              saveDisabled={!draft.name?.trim()}
              updateLoading={isSaving}
            />
          </>
        }
      >
        {device.name}
      </MetaField>

      <MetaField
        label="Адрес"
        editing={editingField === 'host'}
        canEdit={canManage}
        onStartEdit={() => startEdit('host', { host: device.host || '' })}
        editTitle={t('common.edit')}
        mono
        editor={
          <>
            <input
              type="text"
              value={draft.host ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, host: e.target.value }))}
              className={`${fieldInput} font-mono`}
              disabled={isSaving}
            />
            <EditButtons
              onSave={() => onSave({ host: draft.host?.trim() })}
              onCancel={onCancelEdit}
              saveDisabled={!draft.host?.trim()}
              updateLoading={isSaving}
            />
          </>
        }
      >
        {device.host || '—'}
      </MetaField>

      <MetaField
        label="Порт"
        editing={editingField === 'port'}
        canEdit={canManage}
        onStartEdit={() => startEdit('port', { port: String(device.port ?? '') })}
        editTitle={t('common.edit')}
        mono
        editor={
          <>
            <input
              type="number"
              min="1"
              max="65535"
              value={draft.port ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, port: e.target.value }))}
              className={`${fieldInput} w-24`}
              disabled={isSaving}
            />
            <EditButtons
              onSave={() => onSave({ port: parseInt(draft.port, 10) })}
              onCancel={onCancelEdit}
              saveDisabled={!draft.port}
              updateLoading={isSaving}
            />
          </>
        }
      >
        {device.port ?? '—'}
      </MetaField>

      <MetaField
        label="RTSP"
        editing={editingField === 'rtsp'}
        canEdit={canManage}
        onStartEdit={() => startEdit('rtsp', { rtsp_port: String(device.rtsp_port || 554) })}
        editTitle={t('common.edit')}
        mono
        editor={
          <>
            <input
              type="number"
              min="1"
              max="65535"
              value={draft.rtsp_port ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, rtsp_port: e.target.value }))}
              className={`${fieldInput} w-24`}
              disabled={isSaving}
            />
            <EditButtons
              onSave={() => onSave({ rtsp_port: parseInt(draft.rtsp_port, 10) || 554 })}
              onCancel={onCancelEdit}
              saveDisabled={!draft.rtsp_port}
              updateLoading={isSaving}
            />
          </>
        }
      >
        {device.rtsp_port || 554}
      </MetaField>

      <div className="flex min-w-0 items-center gap-2">
        <span className="w-36 shrink-0 text-xs text-muted-500">Живое видео</span>
        {canManage ? (
          <button
            type="button"
            onClick={() => onSave({ video_enabled: device.video_enabled === false })}
            disabled={isSaving}
            className={`inline-flex items-center gap-1.5 text-sm transition ${
              device.video_enabled === false ? 'text-muted-400' : 'text-positive'
            } disabled:opacity-50`}
          >
            <span className="material-icons text-sm">
              {device.video_enabled === false ? 'videocam_off' : 'videocam'}
            </span>
            {device.video_enabled === false ? 'Выключено' : 'Включено'}
          </button>
        ) : (
          <span className="text-sm text-white">
            {device.video_enabled === false ? 'Выключено' : 'Включено'}
          </span>
        )}
      </div>

      {extraFields}

      <MetaField
        label="Логин"
        editing={editingField === 'username'}
        canEdit={canManage}
        onStartEdit={() => startEdit('username', { hik_username: device.hik_username || '' })}
        editTitle={t('common.edit')}
        mono
        editor={
          <>
            <input
              type="text"
              value={draft.hik_username ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, hik_username: e.target.value }))}
              className={`${fieldInput} font-mono`}
              disabled={isSaving}
              autoComplete="off"
            />
            <EditButtons
              onSave={() => onSave({ hik_username: draft.hik_username?.trim() || '' })}
              onCancel={onCancelEdit}
              saveDisabled={!draft.hik_username?.trim() || !device.has_credentials}
              updateLoading={isSaving}
            />
          </>
        }
      >
        {device.hik_username || '—'}
      </MetaField>

      <MetaField label="SN" mono>
        <span className="inline-flex items-center gap-2">
          <span className="truncate">{serial || '—'}</span>
          {serial && (
            <button type="button" onClick={copySerial} className="text-muted-500 hover:text-white">
              <span className="material-icons text-[14px]">content_copy</span>
            </button>
          )}
        </span>
      </MetaField>

      <MetaField
        label="Пароль"
        editing={editingField === 'password'}
        canEdit={canManage}
        onStartEdit={() =>
          startEdit('password', {
            hik_password: '',
            hik_username: device.hik_username || '',
          })
        }
        editTitle={t('common.edit')}
        editor={
          <>
            {!device.hik_username && (
              <input
                type="text"
                placeholder="Логин"
                value={draft.hik_username ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, hik_username: e.target.value }))}
                className={`${fieldInput} font-mono`}
                disabled={isSaving}
                autoComplete="off"
              />
            )}
            <input
              type="password"
              placeholder="Новый пароль"
              value={draft.hik_password ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, hik_password: e.target.value }))}
              className={fieldInput}
              disabled={isSaving}
              autoComplete="new-password"
            />
            <EditButtons
              onSave={() =>
                onSave({
                  hik_username: (draft.hik_username || device.hik_username || '').trim(),
                  hik_password: draft.hik_password,
                })
              }
              onCancel={onCancelEdit}
              saveDisabled={!draft.hik_password || !(draft.hik_username || device.hik_username)}
              updateLoading={isSaving}
            />
          </>
        }
      >
        {device.has_credentials ? '••••••' : '—'}
      </MetaField>

      <MetaField
        label={t('device.protocol')}
        editing={editingField === 'https'}
        canEdit={canManage}
        onStartEdit={() => startEdit('https', { use_https: !!device.use_https })}
        editTitle={t('common.edit')}
        editor={
          <>
            <select
              value={draft.use_https ? '1' : '0'}
              onChange={(e) => setDraft((d) => ({ ...d, use_https: e.target.value === '1' }))}
              className="min-w-[140px] rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              disabled={isSaving}
            >
              <option value="1">HTTPS</option>
              <option value="0">HTTP</option>
            </select>
            <EditButtons onSave={() => onSave({ use_https: !!draft.use_https })} onCancel={onCancelEdit} updateLoading={isSaving} />
          </>
        }
      >
        {device.use_https ? 'HTTPS' : 'HTTP'}
      </MetaField>

      <MetaField label={t('device.scrape')}>{formatMetricValue(metrics?.scrape_duration_seconds, 'сек')}</MetaField>

      {saveError && <p className="sm:col-span-2 text-xs text-critical">{saveError}</p>}
      {checkResult && (
        <p className={`sm:col-span-2 text-xs ${checkResult.reachable ? 'text-positive' : 'text-critical'}`}>
          {checkResult.message}
          {checkResult.reachable && (checkResult.firmware || checkResult.serial)
            ? ` · ${[checkResult.firmware, checkResult.serial].filter(Boolean).join(' · ')}`
            : ''}
        </p>
      )}
      {canManage && (
        <div className="col-span-full mt-2 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCheckReachability}
            disabled={isChecking || isDeleting}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-muted-300 transition hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
          >
            <span className={`material-icons text-[16px] ${isChecking ? 'animate-spin' : ''}`}>
              {isChecking ? 'progress_activity' : 'wifi_tethering'}
            </span>
            {isChecking ? t('schoolDashboard.hikChecking') : t('schoolDashboard.hikCheckReachability')}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting || isChecking}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-muted-500 transition hover:bg-critical/10 hover:text-critical disabled:opacity-50"
          >
            <span className={`material-icons text-[16px] ${isDeleting ? 'animate-spin' : ''}`}>
              {isDeleting ? 'refresh' : 'delete'}
            </span>
            {t('device.delete')}
          </button>
        </div>
      )}
      {deleteError && <p className="col-span-full text-xs text-critical">{deleteError}</p>}
    </div>
  )
}

export default HikvisionDeviceParams
